import { useState } from 'react'

import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useAuthToken,
  useCancelEmailUpdate,
  useResendVerificationCode,
  useUpdateMember,
} from '@tribeplatform/react-sdk/hooks'
import { ActionPanel } from '@tribeplatform/react-ui-kit/ActionPanel'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const ChangeEmail = () => {
  const { $t } = useI18n()
  const { data: member } = useAuthMember()
  const { mutateAsync: updateEmail, isLoading: updateEmailLoading } =
    useUpdateMember()
  const {
    data: { network },
  } = useAuthToken()
  const settingsUrl = network?.activeSso?.settingsUrl
  const { mutate: cancelEmailUpdate } = useCancelEmailUpdate()
  const { mutateAsync: resendVerification } = useResendVerificationCode()
  const [resentEmail, setResentEmail] = useState(false)
  const newEmailPending = !!member?.newEmail

  const onResend = async () => {
    const result = await resendVerification()
    setResentEmail(true)
    if (result?.status === 'succeeded') {
      toast({
        title: $t({
          defaultMessage: 'Verification email sent',
          id: 'Auth.VerificationCode.Email.Sent',
        }),
        description: $t({
          defaultMessage:
            'Please check your spam if you couldn’t find the email in your inbox.',
          id: 'Auth.VerificationCode.Email.Sent.Description',
        }),
        status: 'neutral',
      })
    } else {
      toast({
        title: $t({
          defaultMessage: 'Could not send verification email',
          id: 'Auth.VerificationCode.Email.NotSent',
        }),
        description: $t({
          defaultMessage: 'Something went wrong. Please try again later.',
          id: 'Generics.SomethingWentWrongTryAgainLater',
        }),
        status: 'error',
      })
    }
  }

  if (settingsUrl) {
    return (
      <ActionPanel
        title="Email"
        description={member?.email}
        placement="trailing-top"
      >
        <Button variant="secondaryNeutral" size="lg">
          <Link
            href={settingsUrl}
            variant="neutral"
            target="_blank"
            rel="noopener noreferrer"
          >
            <T defaultMessage="Edit Email" id="Settings.Email.Edit" />
          </Link>
        </Button>
      </ActionPanel>
    )
  }
  return (
    <Card>
      <Card.Header
        title={<T defaultMessage="Email" id="Generics.Email" />}
        description={
          <T
            defaultMessage="Manage how you log in to your account."
            id="Settings.Email.Description"
          />
        }
      />
      <Card.Content>
        <Form
          defaultValues={{ email: member.email }}
          onSubmit={async (data, methods) => {
            const { email } = data
            const { setError } = methods

            if (email === member.email) {
              setError('email', {
                message: $t({
                  defaultMessage:
                    'Please enter a new email address. You are already using this email address.',
                  id: 'Settings.Email.Validation.Same',
                }),

                type: 'validate',
              })
              return
            }

            await updateEmail({
              input: {
                email,
              },
            })
          }}
        >
          {({ formState: { isDirty } }) => (
            <div className="flex flex-col space-y-5">
              <Form.Input
                name="email"
                label={<T defaultMessage="Email" id="Generics.Email" />}
                placeholder="john.smith@acme.com"
                type="email"
                helperText={
                  newEmailPending ? (
                    <>
                      <T
                        defaultMessage="We have sent an email to {email_address}. Please use the instruction in that email to confirm your new email address. You can also <cancel-link>cancel the request</cancel-link>{already_sent, select, false{ or <resend-link>ask us to resend the instructions</resend-link>} other {}}."
                        id="Settings.Email.Confirmation.Pending"
                        values={{
                          email_address: <strong>{member.newEmail}</strong>,
                          'cancel-link': chunks => (
                            <Link
                              onClick={() => {
                                cancelEmailUpdate({})
                              }}
                            >
                              {chunks}
                            </Link>
                          ),
                          'resend-link': chunks => (
                            <Link onClick={onResend}>{chunks}</Link>
                          ),
                          already_sent: resentEmail,
                        }}
                      />
                    </>
                  ) : (
                    <T
                      defaultMessage="We’ll send you an email confirmation when you update your email."
                      id="Settings.Email.Confirmation.PreChangeNotice"
                    />
                  )
                }
              />
              <Form.Actions>
                <Button
                  type="submit"
                  loading={updateEmailLoading}
                  disabled={!isDirty}
                  size="lg"
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </div>
          )}
        </Form>
      </Card.Content>
    </Card>
  )
}
